import { useNavigate } from "react-router-dom";

import { Button } from "components/ui/Button";
import { Notice, NoticeList, NoticeText } from "components/ui/Notice";
import {
  getMissingHseDetails,
  HSE_REQUIRED_FIELDS,
} from "features/healthlink/utils/hsePatientReadiness";
import { pathGenerators } from "utils/routes";

// "Nothing can be sent for this patient until these details are recorded."
//
// A patient can be saved without them — a walk-in or a phone booking should not be
// blocked on paperwork — but every HSE message needs them, so the GP is told when the
// screen opens rather than after filling a form and pressing send (KIN-1733). Shown on
// the list view as well as inside the form: the point is to be read before the work,
// and the list is where the work starts.
//
// Renders nothing when the patient is fine, still loading, or not yet chosen. A notice
// that flashes while data arrives is worse than no notice: people learn to dismiss it.
//
// `requiredFields` is the caller's, not a global list — the integrations differ, and a
// warning that names a field this particular submission does not need teaches the same
// dismissal. Date of birth and gender are the default: every builder sets PID.7 and PID.8.
//
// `submissionName` is named for what the GP is doing, not for the system at the other end.
// It read "to NIIS" on the vaccine screens until Darren pointed out that most users do not
// know what NIIS is; it is now "as a Healthlink vaccine", which is the label on the tab they
// are looking at.
export const HseDetailsNotice = ({
  patient,
  patientId,
  requiredFields = HSE_REQUIRED_FIELDS,
  submissionName = "to the HSE",
}) => {
  const navigate = useNavigate();
  const missing = getMissingHseDetails(patient, requiredFields);

  if (missing.length === 0) return null;

  const editablePatientId = patientId ?? patient?.id;
  const hasSeveralReasons = missing.length > 1;

  return (
    <Notice
      testId="hse-details-notice"
      isStacked={hasSeveralReasons}
      action={
        !!editablePatientId && (
          <Button
            variant="outline"
            size="small"
            onClick={() =>
              navigate(pathGenerators.patient.details(editablePatientId))
            }
          >
            Edit patient details
          </Button>
        )
      }
    >
      <NoticeText isLead={hasSeveralReasons}>
        {hasSeveralReasons
          ? `This patient's details are incomplete, so nothing can be sent ${submissionName} for them:`
          : `This patient has ${missing[0].reason}, so nothing can be sent ${submissionName} for them.`}
      </NoticeText>
      {hasSeveralReasons && (
        <NoticeList>
          {missing.map((detail) => (
            <li key={detail.field}>{detail.reason}</li>
          ))}
        </NoticeList>
      )}
    </Notice>
  );
};
